import { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { authGet } from "../../lib";

const useStyles = makeStyles((theme) => ({
  root: { maxWidth: 960, width: "100%", margin: "0 auto", padding: theme.spacing(3) },
  section: { marginBottom: theme.spacing(3) },
  sectionTitle: { fontWeight: 600, marginBottom: theme.spacing(1) },
  row: { display: "flex", alignItems: "center", gap: theme.spacing(1), marginBottom: theme.spacing(1) },
  result: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.5),
    backgroundColor: "#1e1e1e",
    borderRadius: 4,
    maxHeight: 400,
    overflowY: "auto",
    fontFamily: "monospace",
    fontSize: "0.78rem",
    color: "#d4d4d4",
    whiteSpace: "pre-wrap",
    wordBreak: "break-all",
  },
  error: { color: "#f87171" },
  count: { color: "#86efac", fontWeight: 600 },
  input: { width: 220 },
  runBtn: { textTransform: "none", minWidth: 80 },
}));

function Section({ title, children }) {
  const classes = useStyles();
  return (
    <Paper variant="outlined" style={{ padding: 16, marginBottom: 20 }}>
      <Typography variant="subtitle1" className={classes.sectionTitle}>{title}</Typography>
      <Divider style={{ marginBottom: 12 }} />
      {children}
    </Paper>
  );
}

function ResultBox({ state }) {
  const classes = useStyles();
  if (!state) return null;
  if (state.loading) return <Box mt={1}><CircularProgress size={20} /></Box>;

  const isError = !!state.error;
  const text = isError
    ? state.error
    : JSON.stringify(state.data, null, 2);

  const summary = !isError && Array.isArray(state.data)
    ? <Typography variant="caption" className={classes.count}>{state.data.length} result(s)</Typography>
    : null;

  return (
    <Box>
      {summary}
      <div className={`${classes.result} ${isError ? classes.error : ""}`}>{text}</div>
    </Box>
  );
}

async function run(setter, path) {
  setter({ loading: true });
  const { data, error } = await authGet(path);
  if (error) {
    const msg = error?.message || error?.err || JSON.stringify(error) || "Request failed";
    setter({ error: msg });
  } else {
    setter({ data });
  }
}

export function WatchesPullPage() {
  const classes = useStyles();

  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().split("T")[0];
  });
  const [itemId, setItemId]   = useState("");
  const [groupId, setGroupId] = useState("");

  const [createdRes, setCreatedRes]   = useState(null);
  const [updatedRes, setUpdatedRes]   = useState(null);
  const [productRes, setProductRes]   = useState(null);
  const [brandsRes, setBrandsRes]     = useState(null);
  const [seriesRes, setSeriesRes]     = useState(null);
  const [grpCreatedRes, setGrpCreatedRes] = useState(null);
  const [grpUpdatedRes, setGrpUpdatedRes] = useState(null);
  const [groupRes, setGroupRes]       = useState(null);

  return (
    <Box className={classes.root}>
      <Typography variant="h6" gutterBottom>Watches Pull Endpoint Tester</Typography>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 20 }}>
        Tests your PT pull endpoints using admin auth. These are the same endpoints TPC's consumer will call.
      </Typography>

      {/* Shared inputs */}
      <Box className={classes.row} style={{ marginBottom: 24 }}>
        <TextField
          label="From Date"
          type="date"
          size="small"
          variant="outlined"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          className={classes.input}
        />
        <TextField
          label="Item ID"
          type="number"
          size="small"
          variant="outlined"
          value={itemId}
          onChange={(e) => setItemId(e.target.value)}
          className={classes.input}
          style={{ width: 140 }}
        />
        <TextField
          label="Group ID"
          type="number"
          size="small"
          variant="outlined"
          value={groupId}
          onChange={(e) => setGroupId(e.target.value)}
          className={classes.input}
          style={{ width: 140 }}
        />
      </Box>

      {/* Products */}
      <Section title="Products — Identifier Lists">
        <Box className={classes.row}>
          <Button
            variant="contained" color="primary" size="small" className={classes.runBtn}
            disabled={!fromDate}
            onClick={() => run(setCreatedRes, `/watches/admin/test/products-created?fromDate=${fromDate}`)}
          >
            Created
          </Button>
          <Typography variant="caption" color="textSecondary">
            Items with act_date_added &gt;= {fromDate}
          </Typography>
        </Box>
        <ResultBox state={createdRes} />

        <Box className={classes.row} style={{ marginTop: 12 }}>
          <Button
            variant="contained" color="primary" size="small" className={classes.runBtn}
            disabled={!fromDate}
            onClick={() => run(setUpdatedRes, `/watches/admin/test/products-updated?fromDate=${fromDate}`)}
          >
            Updated
          </Button>
          <Typography variant="caption" color="textSecondary">
            Items with updated_at &gt;= {fromDate}
          </Typography>
        </Box>
        <ResultBox state={updatedRes} />
      </Section>

      {/* Single product payload */}
      <Section title="Product Payload (single item)">
        <Box className={classes.row}>
          <Button
            variant="contained" color="primary" size="small" className={classes.runBtn}
            disabled={!itemId}
            onClick={() => run(setProductRes, `/watches/admin/test/product/${itemId}`)}
          >
            Run
          </Button>
          <Typography variant="caption" color="textSecondary">
            Full flat payload for Item ID {itemId || "—"} (what TPC receives per product)
          </Typography>
        </Box>
        <ResultBox state={productRes} />
      </Section>

      {/* Attributes */}
      <Section title="Attribute Options">
        <Box className={classes.row}>
          <Button
            variant="contained" color="primary" size="small" className={classes.runBtn}
            onClick={() => run(setBrandsRes, `/watches/admin/test/attributes?code=brand`)}
          >
            Brands
          </Button>
          <Button
            variant="contained" color="primary" size="small" className={classes.runBtn}
            onClick={() => run(setSeriesRes, `/watches/admin/test/attributes?code=series`)}
          >
            Series
          </Button>
          <Typography variant="caption" color="textSecondary">
            All brand / series options (value + label pairs)
          </Typography>
        </Box>
        <ResultBox state={brandsRes} />
        <ResultBox state={seriesRes} />
      </Section>

      {/* Series groups */}
      <Section title="Series Groups — Identifier Lists">
        <Box className={classes.row}>
          <Button
            variant="contained" color="primary" size="small" className={classes.runBtn}
            disabled={!fromDate}
            onClick={() => run(setGrpCreatedRes, `/watches/admin/test/groups-created?fromDate=${fromDate}`)}
          >
            Created
          </Button>
          <Button
            variant="contained" color="primary" size="small" className={classes.runBtn}
            disabled={!fromDate}
            onClick={() => run(setGrpUpdatedRes, `/watches/admin/test/groups-updated?fromDate=${fromDate}`)}
          >
            Updated
          </Button>
          <Typography variant="caption" color="textSecondary">
            Series group IDs created / updated since {fromDate}
          </Typography>
        </Box>
        <ResultBox state={grpCreatedRes} />
        <ResultBox state={grpUpdatedRes} />
      </Section>

      {/* Single group payload */}
      <Section title="Group Payload (single series group)">
        <Box className={classes.row}>
          <Button
            variant="contained" color="primary" size="small" className={classes.runBtn}
            disabled={!groupId}
            onClick={() => run(setGroupRes, `/watches/admin/test/group/${groupId}`)}
          >
            Run
          </Button>
          <Typography variant="caption" color="textSecondary">
            Full group payload for Group ID {groupId || "—"}
          </Typography>
        </Box>
        <ResultBox state={groupRes} />
      </Section>
    </Box>
  );
}